import { Plus, ShoppingCart, Trash2 } from "lucide-react";
import { useState } from "react";
import { ProductSelect } from "@/components/products/ProductSelect";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type OrderLineItem, useAddOrderItem, useRemoveOrderItem } from "@/hooks/use-orders";
import { formatCurrency } from "@/lib/format";

interface OrderLineItemsProps {
  orderId: string;
  lineItems: OrderLineItem[];
  totalCents: number;
}

export function OrderLineItems({ orderId, lineItems, totalCents }: OrderLineItemsProps) {
  const [productId, setProductId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState("1");
  // Bumped on each successful add to remount the product combobox, clearing its label.
  const [selectKey, setSelectKey] = useState(0);
  const [removeItem, setRemoveItem] = useState<{ id: string; name: string } | null>(null);

  const addItem = useAddOrderItem(orderId);
  const removeLineItem = useRemoveOrderItem(orderId);

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const qty = Number(quantity);
    if (!productId || !Number.isInteger(qty) || qty < 1) return;
    addItem.mutate(
      { productId, quantity: qty },
      {
        onSuccess: () => {
          setProductId(null);
          setQuantity("1");
          setSelectKey((k) => k + 1);
        },
      },
    );
  }

  function handleRemoveConfirm() {
    if (!removeItem) return;
    removeLineItem.mutate(removeItem.id, {
      onSuccess: () => setRemoveItem(null),
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium">Line items ({lineItems.length})</h3>
        </div>
        <div className="text-sm">
          <span className="text-muted-foreground">Total </span>
          <span className="font-semibold">{formatCurrency(totalCents)}</span>
        </div>
      </div>

      {/* Line item list */}
      {lineItems.length === 0 ? (
        <p className="text-sm text-muted-foreground">No line items yet.</p>
      ) : (
        <div className="space-y-2">
          {lineItems.map((line) => (
            <div
              key={line.id}
              className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3"
            >
              <div className="space-y-0.5">
                <p className="text-sm font-medium">{line.productName}</p>
                <p className="text-xs text-muted-foreground font-mono">{line.productSku}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">
                  {line.quantity} × {formatCurrency(line.unitPriceCents)}
                </span>
                <span className="text-sm font-medium w-20 text-right">
                  {formatCurrency(line.quantity * line.unitPriceCents)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                  onClick={() => setRemoveItem({ id: line.id, name: line.productName })}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add line item form */}
      <form onSubmit={handleAdd} className="flex items-end gap-2">
        <div className="flex-1">
          <ProductSelect key={selectKey} value={productId} onChange={(id) => setProductId(id)} />
        </div>
        <Input
          type="number"
          min="1"
          step="1"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          className="w-20"
          aria-label="Quantity"
        />
        <Button type="submit" size="sm" disabled={addItem.isPending || !productId}>
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </form>

      <ConfirmDialog
        open={removeItem !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveItem(null);
        }}
        title="Remove line item"
        description={`Remove ${removeItem?.name ?? "this product"} from the order? This action cannot be undone.`}
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={handleRemoveConfirm}
      />
    </div>
  );
}
